import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Shield, Users, Eye, EyeOff, Info } from "lucide-react";

/* ── Reusable toggle switch ────────────────────────────────────────────── */
function Toggle({ checked, onChange, id }) {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-primary/40 focus:ring-offset-2 ${
        checked ? "bg-brand-primary" : "bg-slate-300"
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${
          checked ? "translate-x-5" : "translate-x-0"
        }`}
      />
    </button>
  );
}

export default function SitePanel() {
  const [site, setSite] = useState(null);
  const [tenantSettings, setTenantSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    api.get("/admin/site").then((r) => setSite(r.data || {}));
    api
      .get("/admin/tenant-settings")
      .then((r) => setTenantSettings(r.data || {}));
  }, []);

  if (!site || !tenantSettings)
    return <div className="text-slate-500">Loading...</div>;

  /* ── Site form save ── */
  const saveSite = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/site", site);
      toast.success("Saved");
    } catch {
      toast.error("Failed");
    }
  };
  const set = (k) => (e) => setSite({ ...site, [k]: e.target.value });

  /* ── Tenant settings save ── */
  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put("/admin/tenant-settings", {
        show_all_customers_to_dealer: Boolean(
          tenantSettings.show_all_customers_to_dealer
        ),
        show_all_customers_to_agent: Boolean(
          tenantSettings.show_all_customers_to_agent
        ),
      });
      toast.success("Privacy settings saved");
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const toggleSetting = (key) => (val) => {
    setTenantSettings((prev) => ({ ...prev, [key]: val }));
  };

  return (
    <div className="max-w-xl space-y-8">
      {/* ── Site Settings (existing) ── */}
      <div>
        <div className="mb-6">
          <h3 className="font-heading font-bold text-2xl text-brand-nav">
            Site Settings
          </h3>
          <p className="text-slate-500 mt-1">
            Configure your customer support details below. These will be visible
            to your dealers and customers on their profile pages.
          </p>
        </div>
        <form
          onSubmit={saveSite}
          className="bg-white border border-slate-200 rounded-xl p-6 space-y-4 shadow-sm"
        >
          <h4 className="font-heading font-semibold text-lg border-b pb-2 mb-4">
            Customer Support Details
          </h4>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Support Email
              </label>
              <input
                type="email"
                value={site.email || ""}
                onChange={set("email")}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brand-primary/20"
                placeholder="help@yourbrand.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Support Phone
              </label>
              <input
                type="text"
                value={site.phone || ""}
                onChange={set("phone")}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brand-primary/20"
                placeholder="+91 9876543210"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Support WhatsApp
              </label>
              <input
                type="text"
                value={site.whatsapp || ""}
                onChange={set("whatsapp")}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brand-primary/20"
                placeholder="+91 9876543210"
              />
            </div>
          </div>
          <div className="pt-4">
            <button
              type="submit"
              className="bg-brand-primary hover:bg-brand-primaryHover text-white font-semibold px-6 py-2.5 rounded-lg shadow-sm transition-colors"
              data-testid="admin-site-save"
            >
              Save changes
            </button>
          </div>
        </form>
      </div>

      {/* ── Privacy & Access Control ── */}
      <div>
        <div className="mb-6">
          <h3 className="font-heading font-bold text-2xl text-brand-nav flex items-center gap-2">
            <Shield className="w-6 h-6" />
            Privacy & Access Control
          </h3>
          <p className="text-slate-500 mt-1">
            Control whether dealers and agents see all customer records or only their assigned customers.
          </p>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-6">
          {/* Dealer Settings */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
              <Users className="w-4 h-4 text-brand-primary" />
              <h4 className="font-heading font-semibold text-base text-brand-nav">
                Dealer Settings
              </h4>
            </div>
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                  {tenantSettings.show_all_customers_to_dealer ? (
                    <Eye className="w-4 h-4 text-green-600" />
                  ) : (
                    <EyeOff className="w-4 h-4 text-slate-400" />
                  )}
                  Show All Customer List to Dealers
                </p>
                <p className="text-xs text-slate-500 mt-0.5 ml-6">
                  {tenantSettings.show_all_customers_to_dealer
                    ? "Dealers can view all customers (both assigned and un-assigned)."
                    : "Dealers can only view their own assigned customer list (default)."}
                </p>
              </div>
              <Toggle
                id="toggle-dealer-customer-list"
                checked={Boolean(tenantSettings.show_all_customers_to_dealer)}
                onChange={toggleSetting("show_all_customers_to_dealer")}
              />
            </div>
          </div>

          {/* Agent Settings */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
              <Users className="w-4 h-4 text-indigo-500" />
              <h4 className="font-heading font-semibold text-base text-brand-nav">
                Agent Settings
              </h4>
            </div>
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                  {tenantSettings.show_all_customers_to_agent ? (
                    <Eye className="w-4 h-4 text-green-600" />
                  ) : (
                    <EyeOff className="w-4 h-4 text-slate-400" />
                  )}
                  Show All Customer List to Agents
                </p>
                <p className="text-xs text-slate-500 mt-0.5 ml-6">
                  {tenantSettings.show_all_customers_to_agent
                    ? "Agents can view all customers (both assigned and un-assigned)."
                    : "Agents can only view their own assigned customer list (default)."}
                </p>
              </div>
              <Toggle
                id="toggle-agent-customer-list"
                checked={Boolean(tenantSettings.show_all_customers_to_agent)}
                onChange={toggleSetting("show_all_customers_to_agent")}
              />
            </div>
          </div>

          {/* Phone masking info */}
          <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-lg p-3">
            <Info className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
            <p className="text-xs text-blue-700">
              Customer phone numbers are always masked (only last 4 digits
              visible, e.g. ******6789) for all dealers and agents. This cannot
              be disabled.
            </p>
          </div>

          {/* Save */}
          <div className="pt-2">
            <button
              type="button"
              onClick={saveSettings}
              disabled={savingSettings}
              className="bg-brand-primary hover:bg-brand-primaryHover text-white font-semibold px-6 py-2.5 rounded-lg shadow-sm transition-colors disabled:opacity-50"
              data-testid="admin-tenant-settings-save"
            >
              {savingSettings ? "Saving..." : "Save changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
